import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import type { Filter } from '@/app/common/filters/core/filtersTypes.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import {
  ACCOMMODATION_FACILITY_FILTER_NAMES,
  FACILITY_GROUP_DEFINITIONS,
  FACILITY_GROUP_FILTER_NAMES,
} from '@/app/common/filters/groups/facilityGroups.ts';

type FilterDefinition = {
  urlParam: URLParameters;
  writesDefault?: true;
  keepOnReset?: true;
};

export const FILTER_DEFINITIONS: Partial<Record<FILTER_NAME, FilterDefinition>> = {
  [FILTER_NAME.ACCOMMODATION_BEACH]: { urlParam: URLParameters.ACCOMODATION_BEACH },
  [FILTER_NAME.ACCOMMODATION_CHAIN]: { urlParam: URLParameters.ACCOMODATION_CHAIN },
  [FILTER_NAME.ACCOMMODATION_DISTRICT]: { urlParam: URLParameters.ACCOMODATION_DISTRICT },
  [FILTER_NAME.ACCOMMODATION_SKI_LIFT]: { urlParam: URLParameters.ACCOMODATION_SKILIFT },
  [FILTER_NAME.ACCOMMODATION_STATION]: { urlParam: URLParameters.ACCOMODATION_STATION },
  [FILTER_NAME.BEACH_TYPE]: { urlParam: URLParameters.BEACH_TYPE },
  [FILTER_NAME.BLUE_FLAG]: { urlParam: URLParameters.BLUE_FLAG },
  [FILTER_NAME.BOOK_NOW_PAY_LATER]: { urlParam: URLParameters.BOOK_NOW_PAY_LATER },
  [FILTER_NAME.CLIMATE]: { urlParam: URLParameters.CLIMATE },
  [FILTER_NAME.DESTINATION_AIRPORT]: { urlParam: URLParameters.PRE_FILTER_DESTINATION_AIRPORT },
  [FILTER_NAME.DIRECT_FLIGHT]: { urlParam: URLParameters.DIRECT },
  [FILTER_NAME.ENTERTAINMENT]: { urlParam: URLParameters.ENTERTAINMENTS },
  [FILTER_NAME.FACILITIES_ACTIVITY]: { urlParam: URLParameters.ACTIVITY_ACCOMODATION_FACILITIES },
  [FILTER_NAME.FACILITIES_FAMILY]: { urlParam: URLParameters.FAMILY_ACCOMODATION_FACILITIES },
  [FILTER_NAME.FACILITIES_FOOD]: { urlParam: URLParameters.FOOD_ACCOMODATION_FACILITIES },
  [FILTER_NAME.FACILITIES_GENERAL]: { urlParam: URLParameters.GENERAL_ACCOMODATION_FACILITIES },
  [FILTER_NAME.FACILITIES_WELLNESS]: { urlParam: URLParameters.WELLNESS_ACCOMODATION_FACILITIES },
  ...FACILITY_GROUP_DEFINITIONS,
  [FILTER_NAME.FAUNA]: { urlParam: URLParameters.FAUNA },
  [FILTER_NAME.FLASH_SALES]: { urlParam: URLParameters.FLASH_SALES },
  [FILTER_NAME.FREE_CANCELLATION]: { urlParam: URLParameters.FREE_CANCELLATION },
  [FILTER_NAME.LEISURE_TYPE]: { urlParam: URLParameters.LEISURE },
  [FILTER_NAME.MEAL_PLAN]: { urlParam: URLParameters.MEAL },
  [FILTER_NAME.MY_PRICE]: { urlParam: URLParameters.MY_PRICE },
  [FILTER_NAME.OVERNIGHT_FLIGHTS]: { urlParam: URLParameters.OVERNIGHT_FLIGHTS },
  [FILTER_NAME.PINK_PEOPLE]: { urlParam: URLParameters.PINK_PEOPLE },
  [FILTER_NAME.PRICE_RANGE]: { urlParam: URLParameters.PRICE },
  [FILTER_NAME.PROPERTY_NAME]: { urlParam: URLParameters.ACCOMODATION_NAME },
  [FILTER_NAME.PROPERTY_STYLE]: { urlParam: URLParameters.ACCOMODATION_THEME },
  [FILTER_NAME.PROPERTY_TYPE]: { urlParam: URLParameters.ACCOMODATION_TYPE },
  [FILTER_NAME.RATING]: { urlParam: URLParameters.RATING },
  [FILTER_NAME.SORT]: { urlParam: URLParameters.SORT, writesDefault: true, keepOnReset: true },
  [FILTER_NAME.STARS]: { urlParam: URLParameters.STARS },
  [FILTER_NAME.SUSTAINABILITY_CERTIFICATE]: { urlParam: URLParameters.SUSTAINABILITY_CERTIFICATE },
  [FILTER_NAME.TSH]: { urlParam: URLParameters.TOP_SECRET_HOTEL },
  [FILTER_NAME.WATER_SPORTS]: { urlParam: URLParameters.WATERSPORT },
  [FILTER_NAME.WATER_WAVES]: { urlParam: URLParameters.WATER_WAVES },
};

export const filterDefinitionOf = (name: string): FilterDefinition | undefined =>
  FILTER_DEFINITIONS[name as FILTER_NAME];

export const urlParamOf = (name: string): URLParameters | undefined => filterDefinitionOf(name)?.urlParam;

const FLIGHT_FILTER_PARAMS = [
  URLParameters.OUTBOUND_CONNECTIONS,
  URLParameters.RETURN_CONNECTIONS,
  URLParameters.AIRLINES,
  URLParameters.OUTBOUND_DEPARTURE_AIRPORTS,
  URLParameters.OUTBOUND_ARRIVAL_AIRPORTS,
  URLParameters.RETURN_DEPARTURE_AIRPORTS,
  URLParameters.RETURN_ARRIVAL_AIRPORTS,
  URLParameters.DEPARTURE_TIME_OUTBOUND,
  URLParameters.DEPARTURE_TIME_RETURN,
  URLParameters.FLIGHT_DURATION,
  URLParameters.DEPARTURE_FLIGHTS,
  URLParameters.RETURN_FLIGHTS,
  URLParameters.TRAVEL_FLEXIBILITY,
];

const LEGACY_FILTER_PARAMS = [
  URLParameters.ACCOMODATION_FACILITIES,
  URLParameters.FILTER_VOUCHER_EXTRA,
  URLParameters.CUSTOM_CHECKIN_CHECKOUT,
  URLParameters.FILTER_OPX_FLEXIBLE,
  URLParameters.FILTER_LOYALTY_DEALS,
];

export const SEARCH_FILTER_PARAMS: URLParameters[] = [
  ...new Set([
    ...FLIGHT_FILTER_PARAMS,
    ...LEGACY_FILTER_PARAMS,
    ...Object.values(FILTER_DEFINITIONS)
      .filter((definition) => !definition.keepOnReset)
      .map((definition) => definition.urlParam),
  ]),
];

export const FILTERS_THAT_CAN_FILTER_THEMSELVES = [
  ...ACCOMMODATION_FACILITY_FILTER_NAMES,
  ...FACILITY_GROUP_FILTER_NAMES,
];

export const isFilterThatCanFilterItself = (filters: Filter[]): boolean =>
  filters.some((filter) => FILTERS_THAT_CAN_FILTER_THEMSELVES.includes(filter.name as FILTER_NAME));
